import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../../config/api';

export default function EditFixedEquipmentModal({ isOpen, onClose, equipment, onSuccess }) {
  const [formData, setFormData] = useState({
    equipment_id: '',
    name: '',
    type: 'Other',
    location_description: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    installation_date: '',
    last_inspection_date: '',
    next_inspection_date: '',
    inspector_name: '',
    status: 'Unknown',
    notes: '',
    max_capacity: '',
    power_rating: ''
  });
  const [certificateFile, setCertificateFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);


  // Initialize form data with equipment data
  useEffect(() => {
    if (equipment) {
      setFormData({
        equipment_id: equipment.equipment_id || '',
        name: equipment.name || '',
        type: equipment.type || 'Other',
        location_description: equipment.location_description || '',
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serial_number: equipment.serial_number || '',
        installation_date: equipment.installation_date ? equipment.installation_date.split('T')[0] : '',
        last_inspection_date: equipment.last_inspection_date ? equipment.last_inspection_date.split('T')[0] : '',
        next_inspection_date: equipment.next_inspection_date ? equipment.next_inspection_date.split('T')[0] : '',
        inspector_name: equipment.inspector_name || '',
        status: equipment.status || 'Unknown',
        notes: equipment.notes || '',
        max_capacity: equipment.max_capacity || '',
        power_rating: equipment.power_rating || ''
      });
    }
  }, [equipment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add existing certificate URL if no new file
      if (!certificateFile && equipment.test_certificate_url) {
        formDataToSend.append('existing_certificate_url', equipment.test_certificate_url);
      }
      
      // Add new certificate file if selected
      if (certificateFile) {
        formDataToSend.append('certificate', certificateFile);
      }

      const response = await fetch(getApiEndpoint(`/api/properties/${equipment.property_id}/fixed-equipment/${equipment.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to update equipment');
      }

      const updatedEquipment = await response.json();
      onSuccess(updatedEquipment);
      onClose();
      toast.success('Equipment updated successfully');
    } catch (error) {
      console.error('Error updating equipment:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen || !equipment) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Equipment</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="equipment_id">Equipment ID *</label>
              <input
                type="text"
                id="equipment_id"
                name="equipment_id"
                value={formData.equipment_id}
                onChange={handleInputChange}
                required
                placeholder="e.g., LIFT-001"
              />
            </div>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Main Passenger Lift"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="Lift">Lift</option>
                <option value="Escalator">Escalator</option>
                <option value="Moving Walkway">Moving Walkway</option>
                <option value="Crane">Crane</option>
                <option value="Hoist">Hoist</option>
                <option value="Platform Lift">Platform Lift</option>
                <option value="Goods Lift">Goods Lift</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Unknown">Unknown</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="location_description">Location *</label>
            <input
              type="text"
              id="location_description"
              name="location_description"
              value={formData.location_description}
              onChange={handleInputChange}
              required
              placeholder="e.g., Ground floor main entrance"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="manufacturer">Manufacturer</label>
              <input
                type="text"
                id="manufacturer"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleInputChange}
                placeholder="e.g., Otis, Schindler"
              />
            </div>
            <div className="form-group">
              <label htmlFor="model">Model</label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                placeholder="e.g., Gen2-Premier"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="serial_number">Serial Number</label>
              <input
                type="text"
                id="serial_number"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleInputChange}
                placeholder="e.g., SN123456789"
              />
            </div>
            <div className="form-group">
              <label htmlFor="installation_date">Installation Date</label>
              <input
                type="date"
                id="installation_date"
                name="installation_date"
                value={formData.installation_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="max_capacity">Max Capacity</label>
              <input
                type="text"
                id="max_capacity"
                name="max_capacity"
                value={formData.max_capacity}
                onChange={handleInputChange}
                placeholder="e.g., 8 persons, 1000kg"
              />
            </div>
            <div className="form-group">
              <label htmlFor="power_rating">Power Rating</label>
              <input
                type="text"
                id="power_rating"
                name="power_rating"
                value={formData.power_rating}
                onChange={handleInputChange}
                placeholder="e.g., 15kW"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="last_inspection_date">Last Inspection Date</label>
              <input
                type="date"
                id="last_inspection_date"
                name="last_inspection_date"
                value={formData.last_inspection_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="next_inspection_date">Next Inspection Date</label>
              <input
                type="date"
                id="next_inspection_date"
                name="next_inspection_date"
                value={formData.next_inspection_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="inspector_name">Inspector Name</label>
            <input
              type="text"
              id="inspector_name"
              name="inspector_name"
              value={formData.inspector_name}
              onChange={handleInputChange}
              placeholder="e.g., John Smith"
            />
          </div>

          <div className="form-group">
            <label htmlFor="certificate">Test Certificate</label>
            {equipment.test_certificate_url && (
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                Current: 
                <a 
                  href={getApiEndpoint(`/${equipment.test_certificate_url}`)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ marginLeft: '4px', color: '#1A80E5' }}
                >
                  View existing certificate
                </a>
              </div>
            )}
            <input
              type="file"
              id="certificate"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setCertificateFile(e.target.files[0])}
            />
            <small>
              {equipment.test_certificate_url 
                ? 'Upload new file to replace existing certificate' 
                : 'Supported formats: PDF, DOC, DOCX, JPG, PNG (Max: 10MB)'
              }
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional notes or comments"
              rows="3"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Equipment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 